import {checkExhaustive} from 'external/dev_april_corgi+/js/common/asserts';

import {MaterialValue} from '../types/data';

import {PipeId} from './graph';

export type BaySlot = string;
export type WellSlot = string & {_brand: 'well_slot'};

export interface WellLocation {
  bay: BaySlot;
  well: WellSlot;
}

export interface PointLocation {
  xMm: number;
  yMm: number;
  zMm: number;
}

export interface WellOffset {
  // Relative the well bottom
  xMm: number;
  yMm: number;
  zMm: number;
}

interface AddLabwareDefinitionInstruction {
  kind: 'add_labware_definition';
  definition: object;
}

interface AspirateInstruction {
  kind: 'aspirate';
  channels: number;
  volumeNl: number;
}

interface DispenseInstruction {
  kind: 'dispense';
  channels: number;
  volumeNl: number;
}

interface DropTipInstruction {
  kind: 'drop_tip';
  channels: number;
  // The destination of the back-left-most tip, or undefined for trash
  well: WellLocation | undefined;
}

interface HomeInstruction {
  kind: 'home';
}

interface InitializeInstruction {
  kind: 'initialize';
}

export interface LoadMaterialInstruction {
  kind: 'load_material';
  into: WellLocation;
  material: MaterialValue;
  volumeNl: number;
}

export interface LoadLabwareInstruction {
  kind: 'load_labware';
  into: BaySlot;
  model: string;
}

export interface LoadModuleInstruction {
  kind: 'load_module';
  into: BaySlot;
  module: 'magnetic' | 'temperature' | 'thermocycling' | undefined;
}

interface MoveLabwareInstruction {
  kind: 'move_labware';
  depth: number;
  from: BaySlot;
  to: BaySlot;
}

interface MoveToCoordinatesInstruction {
  kind: 'move_to_coordinates';
  channels: number;
  coordinates: PointLocation;
}

interface MoveToWellInstruction {
  kind: 'move_to_well';
  channels: number;
  offset: WellOffset;
  // When doing multi-channel pipetting, this is the first well in the rectangle
  to: WellLocation;
}

export interface PickUpTipInstruction {
  kind: 'pick_up_tip';
  channels: number;
  // The back-left-most tip to pick up
  well: WellLocation;
}

export interface PrepareToAspirateInstruction {
  kind: 'prepare_to_aspirate';
  channels: number;
  volumeNl: number;
}

export interface StoreMaterialInstruction {
  kind: 'store_material';
  from: WellLocation;
  material: MaterialValue;
  volumeNl: number;
}

interface TemperatureBlockTemperatureInstruction {
  kind: 'temperature_block_temperature';
  at: BaySlot;
  temperatureC: number;
}

interface ThermocycleBlockDeactivateInstruction {
  kind: 'thermocycle_block_deactivate';
  at: BaySlot;
}

interface ThermocycleBlockTemperatureInstruction {
  kind: 'thermocycle_block_temperature';
  at: BaySlot;
  durationUs: number | undefined;
  maxVolumeNl: number;
  temperatureC: number;
}

interface ThermocycleLidDeactivateInstruction {
  kind: 'thermocycle_lid_deactivate';
  at: BaySlot;
}

interface ThermocycleLidHingeInstruction {
  kind: 'thermocycle_lid_hinge';
  at: BaySlot;
  closed: boolean;
}

interface ThermocycleLidTemperatureInstruction {
  kind: 'thermocycle_lid_temperature';
  at: BaySlot;
  temperatureC: number;
}

interface WaitInstruction {
  kind: 'wait';
  durationUs: number;
}

export type Instruction =
  | AddLabwareDefinitionInstruction
  | AspirateInstruction
  | DispenseInstruction
  | DropTipInstruction
  | HomeInstruction
  | InitializeInstruction
  | LoadMaterialInstruction
  | LoadModuleInstruction
  | LoadLabwareInstruction
  | MoveLabwareInstruction
  | MoveToCoordinatesInstruction
  | MoveToWellInstruction
  | PickUpTipInstruction
  | PrepareToAspirateInstruction
  | StoreMaterialInstruction
  | TemperatureBlockTemperatureInstruction
  | ThermocycleBlockDeactivateInstruction
  | ThermocycleBlockTemperatureInstruction
  | ThermocycleLidDeactivateInstruction
  | ThermocycleLidHingeInstruction
  | ThermocycleLidTemperatureInstruction
  | WaitInstruction;

export const REMAINDER_WELL_VOLUME_NL = 1_000;

export function getSimulationScript(
  name: string,
  instructionsByPipe: Array<{pipe: PipeId; instructions: Instruction[]}>,
  authorName: string,
  robotName: string,
): string {
  let program = `from collections import defaultdict
import json
import time

from opentrons import protocol_api
from opentrons.execute import get_protocol_api as get_execute_protocol
from opentrons.simulate import get_protocol_api as get_simulate_protocol
from opentrons.types import Point
from opentrons_shared_data.labware.types import LabwareDefinition

SIMULATE = True

metadata = {
  "protocolName": "${name.replaceAll('"', '\\"')}",
  "author": "${authorName.replaceAll('"', '\\"')}",
  "robot": "${robotName.replaceAll('"', '\\"')}",
  "source": "petri",
}
requirements = {"robotType": "Flex", "apiLevel": "2.22"}

def run(protocol):
  protocol.home()

  deck = defaultdict(list)
  trash = protocol.load_waste_chute()
  last_pipe = None
`;

  let initialized = false;
  for (const {pipe, instructions} of instructionsByPipe) {
    program += `
  pipe = "${pipe.node}-${pipe.id}"
  if pipe != last_pipe:
    comment(protocol, f"Starting pipe {pipe}")
    last_pipe = pipe
`;
    for (const instruction of instructions) {
      program += `\n  # ${instruction.kind}`;

      if (instruction.kind === 'initialize') {
        if (initialized) {
          throw new Error('The run has already been initialized');
        }
        program += `
  pipette_1_50ul = protocol.load_instrument(instrument_name="flex_1channel_50", mount="left")
  pipette_8_1000ul = protocol.load_instrument(instrument_name="flex_8channel_1000", mount="right")
  channel_configurations = {pipette_1_50ul: 1}

  def get_pipette(channels: int):
    if channels <= 1:
      pipette = pipette_1_50ul
    elif channels <= 8:
      pipette = pipette_8_1000ul
    else:
      raise Exception('no such pipette')

    if channel_configurations.get(pipette, 0) != channels:
      if channels == 1 or channels == 8:
        pipette.configure_nozzle_layout(protocol_api.ALL)
      else:
        pipette.configure_nozzle_layout(
          protocol_api.PARTIAL_COLUMN,
          start="A1",
          end=["B1", "C1", "D1", "E1", "F1", "G1"][channels - 2],
        )
      channel_configurations[pipette] = channels
    return pipette
`;
        initialized = true;
        continue;
      } else if (!initialized) {
        throw new Error('Cannot execute instructions prior to initialization');
      }

      if (instruction.kind === 'add_labware_definition') {
        program += `
  protocol._core.add_labware_definition(
    definition=LabwareDefinition(**(json.loads('${JSON.stringify(instruction.definition)}')))
  )
  `;
      } else if (instruction.kind === 'aspirate') {
        program += `
  get_pipette(${instruction.channels}).aspirate(
    location=None,  # in place
    volume=${instruction.volumeNl} / 1000,
  )
`;
      } else if (instruction.kind === 'dispense') {
        program += `
  get_pipette(${instruction.channels}).dispense(
    location=None,  # in place
    volume=${instruction.volumeNl} / 1000,
  )
`;
      } else if (instruction.kind === 'drop_tip') {
        program += `
  pipette = get_pipette(${instruction.channels})`;
        if (instruction.well) {
          // When configured for partial tip pick-up you cannot return tips to the tiprack. It kind of
          // makes sense (if there are nearby tips they'll get picked up at the same time) but is dumb
          // when you think about it (just drop the tips higher).
          program += `
  # location=deck["${instruction.well.bay}"][-1]["${instruction.well.well}"]
  pipette.drop_tip(trash.top(z=20))
`;
        } else {
          program += `
  pipette.drop_tip(trash.top(z=20))
`;
        }
      } else if (instruction.kind === 'home') {
        program += `
  protocol.home()
`;
      } else if (instruction.kind === 'load_material') {
        const message = [
          `Load ${instruction.volumeNl / 1000}μl of ${instruction.material.label} into`,
          `bay ${instruction.into.bay} well ${instruction.into.well}`,
        ].join(' ');
        program += `
  message = "${message.replaceAll('"', '\\"')}"
  if SIMULATE:
    comment(protocol, f"User performing: {message}")
  else:
    print(message)
    input('(press enter to continue)')
`;
      } else if (instruction.kind === 'load_module') {
        if (instruction.module === 'magnetic') {
          program += `
  magnet = protocol.load_module(
    module_name="magneticBlockV1",
    location="${instruction.into}"
  )
  deck["${instruction.into}"].append(magnet)
`;
        } else if (instruction.module === 'temperature') {
          program += `
  temperature = protocol.load_module(
    module_name="temperatureModuleV2",
    location="${instruction.into}"
  )
  deck["${instruction.into}"].append(temperature)
  `;
        } else if (instruction.module === 'thermocycling') {
          program += `
  thermocycler = protocol.load_module(module_name="thermocyclerModuleV2")
  deck["${instruction.into}"].append(thermocycler)
`;
        } else if (!instruction.module) {
          // do nothing
        } else {
          throw checkExhaustive(instruction.module);
        }
      } else if (instruction.kind === 'load_labware') {
        const clean = instruction.model;
        const message = `Load a clean ${instruction.model} into ${instruction.into}`;
        const method =
          [
            'opentrons_96_well_aluminum_block',
            'opentrons_flex_deck_riser',
          ].indexOf(clean) >= 0
            ? 'load_adapter'
            : 'load_labware';
        program += `
  message = "${message.replaceAll('"', '\\"')}"
  if SIMULATE:
    comment(protocol, f"User performing: {message}")
  else:
    print(message)
    input('(press enter to continue)')

  if onto := top(deck["${instruction.into}"]):
    deck["${instruction.into}"].append(
      onto.${method}(
        "${clean}",
      )
    )
  else:
    deck["${instruction.into}"].append(
      protocol.${method}(
        "${clean}",
        "${instruction.into}",
      )
    )
`;
      } else if (instruction.kind === 'move_labware') {
        program += `
  protocol.move_labware(
    labware=deck["${instruction.from}"][-${instruction.depth}],
    new_location=top(deck["${instruction.to}"]) or "${instruction.to}",
    use_gripper=True,
  )
  deck["${instruction.to}"].extend(deck["${instruction.from}"][-${instruction.depth}:])
  deck["${instruction.from}"] = deck["${instruction.from}"][0:-${instruction.depth}]
`;
      } else if (instruction.kind === 'move_to_coordinates') {
        program += `
  pipette = get_pipette(${instruction.channels})
  pipette.move_to(
    location=Point(
      x=${instruction.coordinates.xMm},
      y=${instruction.coordinates.yMm},
      z=${instruction.coordinates.zMm},
    )
  )
`;
      } else if (instruction.kind === 'move_to_well') {
        program += `
  get_pipette(${instruction.channels}).move_to(location=top(deck["${instruction.to.bay}"])["${instruction.to.well}"].bottom().move(Point(x=${instruction.offset.xMm}, y=${instruction.offset.yMm}, z=${instruction.offset.zMm})))
`;
      } else if (instruction.kind === 'pick_up_tip') {
        program += `
  pipette = get_pipette(${instruction.channels})
  pipette.pick_up_tip(location=deck["${instruction.well.bay}"][-1]["${instruction.well.well}"])
`;
      } else if (instruction.kind === 'prepare_to_aspirate') {
        program +=
          `
  pipette = get_pipette(${instruction.channels}); ` +
          `pipette.configure_for_volume(${instruction.volumeNl} / 1000); ` +
          `pipette.prepare_to_aspirate()
`;
      } else if (instruction.kind === 'store_material') {
        const location = instruction.from;
        program += `
  comment(
      protocol,
      f"Made {round(${instruction.volumeNl} / 1000)}μl of ${instruction.material.label} "
      f"in ${location.bay} well ${location.well}"
  )
`;
      } else if (instruction.kind === 'temperature_block_temperature') {
        program += `
  temperature.set_temperature(celsius=${instruction.temperatureC})
`;
      } else if (instruction.kind === 'thermocycle_block_deactivate') {
        program += `
  thermocycler.deactivate_block()
  `;
      } else if (instruction.kind === 'thermocycle_block_temperature') {
        program += `
  thermocycler.set_block_temperature(
    block_max_volume=${instruction.maxVolumeNl} / 1000,
    hold_time_seconds=${instruction.durationUs ?? 0} / 1000 / 1000 or None,
    temperature=${instruction.temperatureC}
  )
`;
      } else if (instruction.kind === 'thermocycle_lid_deactivate') {
        program += `
  thermocycler.deactivate_lid()
  `;
      } else if (instruction.kind === 'thermocycle_lid_hinge') {
        if (instruction.closed) {
          program += `
  thermocycler.close_lid()
  `;
        } else {
          program += `
  thermocycler.open_lid()
  `;
        }
      } else if (instruction.kind === 'thermocycle_lid_temperature') {
        program += `
  thermocycler.set_lid_temperature(temperature=${instruction.temperatureC})
  `;
      } else if (instruction.kind === 'wait') {
        program += `
  time.sleep(0)  # (${instruction.durationUs} / 1000 / 1000)
   `;
      } else {
        throw checkExhaustive(instruction);
      }
    }
  }

  program += `

def comment(protocol, message):
  from opentrons.legacy_commands.types import COMMAND as COMMAND_TOPIC, COMMENT
  protocol.broker.publish(topic=COMMAND_TOPIC, message={
    "$": "before",
    "id": "unknown",
    "name": COMMENT,
    "payload": {"text": message},
  })
  protocol.broker.publish(topic=COMMAND_TOPIC, message={
    "$": "after",
    "id": "unknown",
    "name": COMMENT,
    "payload": {"text": message},
  })

def top(l: list):
  return l[-1] if len(l) > 0 else None

if SIMULATE:
  protocol = get_simulate_protocol("2.22", robot_type="Flex")
else:
  protocol = get_execute_protocol("2.22")

run(protocol)

if SIMULATE:
  for i in protocol.commands():
    print(i)
`;
  return program;
}
